import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useHistoryBackClose } from "../hooks/useHistoryBackClose";
import { useMediaQuery, BP_TABLET } from "../hooks/useMediaQuery";
import { createPortal } from "react-dom";
import styles from "./WorkoutAdminModal.module.css";
import ModalHeader from "./ModalHeader";
import {
MUSCLE_GROUP_OPTIONS,
EQUIPMENT_OPTIONS,
MOVEMENT_TYPE_OPTIONS,
} from "./workoutOptions";
import ConfirmDialog from "./ConfirmDialog";
import ExerciseDemoModal from "./ExerciseDemoModal";
import { useToast } from "./ToastProvider";
import api from "../services/api";
import WorkoutRationaleModal from "./WorkoutRationaleModal";
import TabButton from "./workoutAdmin/TabButton";
import CatalogTab from "./workoutAdmin/CatalogTab";
import MyVideosTab from "./workoutAdmin/MyVideosTab";
import PlansTab from "./workoutAdmin/PlansTab";
import StudentsTab from "./workoutAdmin/StudentsTab";
import { WorkoutAdminContext } from "../context/WorkoutAdminContext";
import DayVideosAssignmentModal from "./workoutAdmin/DayVideosAssignmentModal";
import PlanModal from "./workoutAdmin/PlanModal";
import AIConfirmModal from "./workoutAdmin/AIConfirmModal";
import ExerciseItemModal from "./workoutAdmin/ExerciseItemModal";
import AIGenerateModal from "./workoutAdmin/AIGenerateModal";
import AddAnonItemModal from "./workoutAdmin/AddAnonItemModal";
import PlanItemModal from "./workoutAdmin/PlanItemModal";
import AssignmentItemModal from "./workoutAdmin/AssignmentItemModal";
import StudentAssignmentModal from "./workoutAdmin/StudentAssignmentModal";
import {
  computeListablePlans,
  getEmptyExerciseItemForm,
  getEmptyPlanForm,
  getEmptyPlanItemForm,
  getEmptyPresetForm,
  parseRepsPerSet,
  parseRestPerSet,
  buildItemBlocks,
  groupLabel,
  findGroupConflicts,
  findSetsMismatch,
  checkMediaFileSize,
  getOptionLabel,
  AI_GENERATING_MESSAGES,
} from "./workoutAdmin/workoutAdminHelpers";

// ── Mensagens rotativas durante geração por IA (mesmo padrão do fluxo self-service) ──


export default function WorkoutAdminModal({ open, onClose, apiBase = "", token, isMobile = false }) {
  useBodyScrollLock();
const [tab, setTab] = useState("catalog");

// Layout de coluna unica (mesmo criterio do CSS, ver "BP_TABLET" no .module.css)
// — abaixo disso, selecionar algo automaticamente pula direto pra tela de
// detalhe do padrao mobile de 2 telas, escondendo a lista antes do usuario
// ver as opcoes (ver refreshAll abaixo). Ref pra nao recriar refreshAll a
// cada troca de largura — so precisa do valor no momento em que os dados
// chegam, nao precisa re-disparar o carregamento quando a tela gira/redimensiona.
const isCollapsedLayout = useMediaQuery(`(max-width: ${BP_TABLET - 1}px)`);
const isCollapsedLayoutRef = useRef(isCollapsedLayout);
useEffect(() => { isCollapsedLayoutRef.current = isCollapsedLayout; }, [isCollapsedLayout]);

const [loading, setLoading] = useState(false);
const [saving, setSaving] = useState(false);

const [currentUser, setCurrentUser] = useState(null);

const [categories, setCategories] = useState([]);
const [selectedCategoryId, setSelectedCategoryId] = useState(null);
const [categoryItems, setCategoryItems] = useState([]);
const [showInactiveExercises, setShowInactiveExercises] = useState(false);
  const [exerciseItemSearch, setExerciseItemSearch] = useState("");

const [exerciseItemForm, setExerciseItemForm] = useState(getEmptyExerciseItemForm());
const [editingExerciseItemId, setEditingExerciseItemId] = useState(null);
const [exerciseItemFile, setExerciseItemFile] = useState(null);
const [openExerciseItemModal, setOpenExerciseItemModal] = useState(false);
const [demoExerciseItem, setDemoExerciseItem] = useState(null);
// Preview dos vídeos de demonstração de um dia inteiro (todos os exercícios
// de um UserWorkoutPlan), antes de abrir o editor completo. Cada item já vem
// com o vídeo EFETIVO resolvido pelo backend (aluno > professor > padrão do
// catálogo — ver serialize_effective_assignment/_resolve_effective_demo em
// app/api/user_workout_plans.py), então aqui só é preciso exibir.
const [dayVideosAssignment, setDayVideosAssignment] = useState(null);

// ── "Meus vídeos" (professor): vídeo de demonstração próprio por
// exercise_item, camada intermediária entre o padrão do catálogo e o vídeo
// específico de um aluno — ver app/models/professor_exercise_media.py.
// Mapa { exercise_item_id: { id, demo_url } } de tudo que o professor logado
// já definiu, carregado uma vez em refreshAll (papel professor).
const [professorMediaByItemId, setProfessorMediaByItemId] = useState({});
const [editingProfessorMediaItemId, setEditingProfessorMediaItemId] = useState(null);
const [professorMediaUrlInput, setProfessorMediaUrlInput] = useState("");
const [savingProfessorMedia, setSavingProfessorMedia] = useState(false);

// Preview do vídeo na hora de prescrever (select de exercício dos modais de
// treino-modelo e de exercício avulso). Mostra o vídeo que o aluno vai ver:
// o próprio do professor, se houver, senão o padrão do catálogo.
const demoUrlFor = useCallback(
  (item) => (item ? professorMediaByItemId[item.id]?.demo_url || item.demo_url || "" : ""),
  [professorMediaByItemId]
);
const previewExerciseDemo = useCallback((item) => {
  const url = demoUrlFor(item);
  if (url) setDemoExerciseItem({ ...item, demo_url: url });
}, [demoUrlFor]);

const [plans, setPlans] = useState([]);
const [selectedPlanId, setSelectedPlanId] = useState(null);
const [selectedPlan, setSelectedPlan] = useState(null);

const [planForm, setPlanForm] = useState(getEmptyPlanForm());
const [editingPlanId, setEditingPlanId] = useState(null);
const [openPlanModal, setOpenPlanModal] = useState(false);

const [planItemForm, setPlanItemForm] = useState(getEmptyPlanItemForm());
const [editingPlanItemId, setEditingPlanItemId] = useState(null);
const [openPlanItemModal, setOpenPlanItemModal] = useState(false);
const [itemsExpanded, setItemsExpanded] = useState(true);
// Bi-set/superset/circuito: modo de seleção múltipla pra agrupar itens
// já existentes na lista (ver buildItemBlocks acima).
const [groupSelectMode, setGroupSelectMode] = useState(false);
const [groupSelectedIds, setGroupSelectedIds] = useState([]);
// Mesma ideia, mas pro modal de "Personalização do aluno" (itens da
// ATRIBUIÇÃO — user_workout_plan_items — não do template). Estado
// separado porque é uma lista/endpoint diferente.
const [groupSelectModeAssignment, setGroupSelectModeAssignment] = useState(false);
const [groupSelectedIdsAssignment, setGroupSelectedIdsAssignment] = useState([]);

const [users, setUsers] = useState([]);
const [selectedStudentId, setSelectedStudentId] = useState(null);
const [studentAssignments, setStudentAssignments] = useState([]);
const [selectedStudentAssignmentId, setSelectedStudentAssignmentId] = useState(null);
const [assignmentPlanId, setAssignmentPlanId] = useState("");
// Combobox com busca do "Treino padrão" (troca o <select> nativo — ver
// auditoria ux-product-designer, recomendacao "Recognition over recall").
const [planPickerOpen, setPlanPickerOpen] = useState(false);
const [planPickerQuery, setPlanPickerQuery] = useState("");
const [planPickerActiveIndex, setPlanPickerActiveIndex] = useState(-1);
const planPickerRef = useRef(null);
const [assignmentItemDraft, setAssignmentItemDraft] = useState(null);
const [openAssignmentItemModal, setOpenAssignmentItemModal] = useState(false);
const [openStudentAssignmentModal, setOpenStudentAssignmentModal] = useState(false);
const [openAddAnonItemModal, setOpenAddAnonItemModal] = useState(false);

// ── Presets de série/descanso (ver app/models/workout_preset.py) ──────────
// Atalho na hora de incluir/editar um exercício: aplica sets_planned/
// reps_planned/rest_seconds/notes de uma vez, sem travar o preenchimento
// manual (que continua funcionando normalmente, antes e depois de aplicar
// um preset). Mostrados como botões/chips direto no form (ver
// PresetPickerInline) — não como uma lista à parte pra escolher, que na
// prática exigia abrir+buscar+aplicar pra um atalho que devia ser 1 clique
// (Fitts's Law / Hick's Law — poucas opções, alvo grande, sem passo extra).
const [presets, setPresets] = useState([]);
const [presetsLoaded, setPresetsLoaded] = useState(false);
const [presetsLoading, setPresetsLoading] = useState(false);
const [showCreatePresetForm, setShowCreatePresetForm] = useState(false);
const [newPresetForm, setNewPresetForm] = useState(getEmptyPresetForm());
const [savingPreset, setSavingPreset] = useState(false);

// ── AI workout generation ──────────────────────────────────────────────────
const [showAIModal, setShowAIModal] = useState(false);
const [aiGenerating, setAiGenerating] = useState(false);
const [aiGenMsgIdx, setAiGenMsgIdx] = useState(0);
const [aiSuccess, setAiSuccess] = useState(null); // { days_created, message, rationale, days } -- preenchido só após /confirm
// Prévia gerada (não persistida ainda) -- espelha o "phase: preview" do SelfWorkoutModal.
// Permite ao professor revisar o resultado e "← Ajustar" antes de decidir manter,
// sem que cada tentativa sobrescreva de imediato o plano do aluno. Ver docs/07-issues.md.
const [aiPreview, setAiPreview] = useState(null); // { days, rationale, rationale_student, ai_usage_log_id }
const [aiConfirming, setAiConfirming] = useState(false);
const [aiHasAdjusted, setAiHasAdjusted] = useState(false); // ajuste permitido 1x por geração, mesmo limite do self-service
const [, setAiRationaleOpen] = useState(true);
  const [rationaleModal, setRationaleModal] = useState({ open: false, text: null });
  const [hasRationale, setHasRationale] = useState(false);
const [aiConfirmOpen, setAiConfirmOpen] = useState(false);
const [aiForm, setAiForm] = useState({
  goals: ["hipertrofia"],
  days_per_week: 4,
  session_duration_minutes: 60,
  restrictions: "",
  focus: "",
  secondary_goals: [],
  include_cardio: false,
  cardio_type: "livre",
  cardio_duration_minutes: 20,
  teacher_notes: "",
  equipment: [],
});

// Rotaciona mensagens durante a geração por IA (mesmo padrão do SelfWorkoutModal)
useEffect(() => {
  if (!aiGenerating) return;
  setAiGenMsgIdx(0);
  const iv = setInterval(
    () => setAiGenMsgIdx((i) => Math.min(i + 1, AI_GENERATING_MESSAGES.length - 1)),
    5000
  );
  return () => clearInterval(iv);
}, [aiGenerating]);

const [assignForm, setAssignForm] = useState({
  is_active: true,
  day_of_week: "",
  custom_name: "",
});
// Preferências do vínculo selecionado (edição)
const [assignmentMetaForm, setAssignmentMetaForm] = useState({ day_of_week: "", custom_name: "" });
const [openPlanActionsMenu, setOpenPlanActionsMenu] = useState(false);
const [planExerciseItems, setPlanExerciseItems] = useState([]);  
const [anonExerciseItems, setAnonExerciseItems] = useState([]);
const [showConfirm, setShowConfirm] = useState(false);
const [confirmMessage, setConfirmMessage] = useState("");
const [confirmLabel, setConfirmLabel] = useState("Confirmar");
// Título e cor do botão da confirmação: só o "Sincronizar" muda (não é uma
// ação destrutiva); voltam ao padrão ao fechar, pra não vazar pras outras.
const [confirmTitle, setConfirmTitle] = useState(undefined);
const [confirmVariant, setConfirmVariant] = useState(undefined);
const confirmActionRef = useRef(null);
const toast = useToast();

// Busca os presets 1x (lazy) — disparado por um useEffect quando qualquer
// um dos 3 modais de item abre (ver abaixo), pra já aparecerem como chips
// assim que o form abre, sem precisar de um clique extra só pra ver a lista.
const loadPresets = useCallback(async () => {
  if (presetsLoaded || presetsLoading) return;
  setPresetsLoading(true);
  try {
    const { data } = await api.get("/workout-presets");
    // App primeiro (professor_id nulo), depois os próprios do professor —
    // mesmo critério do backend (GET /workout-presets), reordenado aqui só
    // por segurança caso a ordem da API mude.
    const sorted = [...(Array.isArray(data) ? data : [])].sort((a, b) => {
      const ownDiff = (a.professor_id != null ? 1 : 0) - (b.professor_id != null ? 1 : 0);
      if (ownDiff !== 0) return ownDiff;
      return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
    });
    setPresets(sorted);
    setPresetsLoaded(true);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Não foi possível carregar os presets.");
  } finally {
    setPresetsLoading(false);
  }
}, [presetsLoaded, presetsLoading, toast]);

useEffect(() => {
  if (openPlanItemModal || openAssignmentItemModal || openAddAnonItemModal) {
    loadPresets();
  }
}, [openPlanItemModal, openAssignmentItemModal, openAddAnonItemModal, loadPresets]);

// Aplica um preset direto no form do fluxo que o chamou (1 clique, sem
// diálogo extra — ver PresetPickerInline). Mesma lógica de "séries mudou"
// que já existe pro preenchimento manual (parseRepsPerSet/parseRestPerSet),
// só que disparada pelo preset em vez de digitação. Não bloqueia edição
// depois: os campos continuam abertos pra ajuste manual.
const applyPreset = useCallback((preset, target) => {
  const n = preset.sets_planned || 0;
  const patch = (prev) => ({
    ...prev,
    sets_planned: preset.sets_planned != null ? String(preset.sets_planned) : prev.sets_planned,
    reps_planned: preset.reps_planned ?? prev.reps_planned,
    reps_per_set: n > 0 ? parseRepsPerSet(preset.reps_planned || "", n) : prev.reps_per_set,
    rest_seconds: preset.rest_seconds ?? prev.rest_seconds,
    rest_per_set: n > 0 ? parseRestPerSet(preset.rest_seconds || "", n) : prev.rest_per_set,
    notes: preset.notes ?? prev.notes,
  });
  if (target === "plan") setPlanItemForm(patch);
  else if (target === "assignment") setAssignmentItemDraft((prev) => (prev ? patch(prev) : prev));
  else if (target === "anon") setAnonItemForm(patch);
}, []);

const handleCreatePreset = useCallback(async () => {
  if (!newPresetForm.name.trim()) {
    toast.error("Dê um nome pro preset.");
    return;
  }
  setSavingPreset(true);
  try {
    const payload = {
      name: newPresetForm.name.trim(),
      sets_planned: newPresetForm.sets_planned ? Number(newPresetForm.sets_planned) : null,
      reps_planned: newPresetForm.reps_planned || null,
      rest_seconds: newPresetForm.rest_seconds || null,
      notes: newPresetForm.notes || null,
    };
    const { data } = await api.post("/workout-presets", payload);
    setPresets((prev) => [...prev, data]);
    setNewPresetForm(getEmptyPresetForm());
    setShowCreatePresetForm(false);
    toast.success("Preset criado.");
  } catch (err) {
    const detail = err?.response?.data?.detail;
    toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Não foi possível criar o preset.");
  } finally {
    setSavingPreset(false);
  }
}, [newPresetForm, toast]);

const handleDeletePreset = useCallback(async (presetId) => {
  try {
    await api.delete(`/workout-presets/${presetId}`);
    setPresets((prev) => prev.filter((p) => p.id !== presetId));
    toast.success("Preset removido.");
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Não foi possível remover o preset.");
  }
}, [toast]);

const confirmDeletePreset = useCallback((preset) => {
  setConfirmMessage(`Remover o preset "${preset.name}"?`);
  setConfirmLabel("Remover");
  confirmActionRef.current = () => handleDeletePreset(preset.id);
  setShowConfirm(true);
}, [handleDeletePreset]);

// Usa o campo oficial do backend
const isBlankPlan = useCallback((plan) => !!plan?.is_blank, []);
// Split padrao da plataforma (sem professor dono) — ver v15_standard_workout_splits
const isStandardPlan = useCallback((plan) => !!plan?.is_standard, []);
// UX: search queries for filtering
const [planQuery, setPlanQuery] = useState("");
const [studentQuery, setStudentQuery] = useState("");

// Map de professores por id para exibir nomes quando o objeto embutido não vier da API
const professorsById = useMemo(() => {
  const map = new Map();
  (users || [])
    .filter((u) => u.role === "professor")
    .forEach((p) => map.set(p.id, p));
  return map;
}, [users]);


const resetFeedback = useCallback(() => {}, []);

const resetExerciseItemEditor = useCallback(() => {
  setEditingExerciseItemId(null);
  setExerciseItemForm(getEmptyExerciseItemForm());
  setExerciseItemFile(null);
  setOpenExerciseItemModal(false);
}, []);

const resetPlanEditor = useCallback(() => {
  setEditingPlanId(null);
  setPlanForm(getEmptyPlanForm());
}, []);

const resetPlanItemEditor = useCallback(() => {
  setEditingPlanItemId(null);
  setPlanItemForm(getEmptyPlanItemForm());
}, []);

const openCreatePlanModal = useCallback(() => {
  resetFeedback();
  resetPlanEditor();
  setOpenPlanModal(true);
}, [resetFeedback, resetPlanEditor]);

const openEditPlanModal = useCallback(
  (plan) => {
    resetFeedback();
    setEditingPlanId(plan.id);
    setPlanForm({
      title: plan.title || "",
      description: plan.description || "",
      is_active: !!plan.is_active,
      is_blank: !!plan.is_blank,
    });
    setOpenPlanModal(true);
  },
  [resetFeedback]
);

const closePlanModal = useCallback(() => {
  setOpenPlanModal(false);
  resetPlanEditor();
}, [resetPlanEditor]);

const openCreatePlanItemModal = useCallback(() => {
  resetFeedback();
  resetPlanItemEditor();
  setPlanExerciseItems([]);
  setOpenPlanItemModal(true);
}, [resetFeedback, resetPlanItemEditor]);

const loadExerciseItemsByCategory = useCallback(
  async (exerciseId, activeOnly = true) => {
    return (await api.get(`/exercise-items/by-category/${exerciseId}`, {
      params: { active_only: activeOnly },
    })).data;
  },
  []
);

const openEditPlanItemModal = useCallback(
  async (item) => {
    resetFeedback();
    setEditingPlanItemId(item.id);

    if (item.exercise_id) {
      try {
        const items = await loadExerciseItemsByCategory(Number(item.exercise_id));
        setPlanExerciseItems(items || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || "Erro ao carregar exercícios da categoria.");
      }
    } else {
      setPlanExerciseItems([]);
    }

    setPlanItemForm({
      exercise_id: item.exercise_id || "",
      exercise_item_id: item.exercise_item_id || "",
      order_index: item.order_index || 0,
      sets_planned: item.sets_planned ?? "",
      reps_planned: item.reps_planned ?? "",
      reps_per_set: item.sets_planned ? parseRepsPerSet(item.reps_planned ?? "", Number(item.sets_planned)) : [],
      duration_planned: item.duration_planned ?? "",
      weight_planned: item.weight_planned ?? "",
      rest_seconds: item.rest_seconds ?? "",
      rest_per_set: item.sets_planned ? parseRestPerSet(item.rest_seconds ?? "", Number(item.sets_planned)) : [],
      notes: item.notes ?? "",
    });

    setOpenPlanItemModal(true);
  },
  [resetFeedback, loadExerciseItemsByCategory, toast]
);

const closePlanItemModal = useCallback(() => {
  setOpenPlanItemModal(false);
  setPlanExerciseItems([]);
  resetPlanItemEditor();
}, [resetPlanItemEditor]);

const loadMe = useCallback(async () => {
  return (await api.get("/users/me")).data;
}, []);

const loadExercises = useCallback(async () => {
  return (await api.get("/exercises")).data;
}, []);

const loadPlans = useCallback(async () => {
  return (await api.get("/workout-plans")).data;
}, []);

const loadPlanDetail = useCallback(
  async (planId) => {
    return (await api.get(`/workout-plans/${planId}`)).data;
  },
  []
);

const refreshSelectedPlan = useCallback(
  async (planId = selectedPlanId) => {
    if (!planId) return;
    const plan = await loadPlanDetail(planId);
    setSelectedPlan(plan);
  },
  [loadPlanDetail, selectedPlanId]
);

const loadUsers = useCallback(async () => {
  try {
    return (await api.get("/users/students", { params: { scope: "org", limit: 500 } })).data;
  } catch (_) {
    return (await api.get("/users")).data;
  }
}, []);

const loadUserWorkoutPlans = useCallback(
  async (userId) => {
    return (await api.get(`/user-workout-plans/users/${userId}?only_active=false`)).data;
  },
  []
);

const loadProfessorExerciseMedia = useCallback(async (professorId) => {
  const rows = (await api.get(`/professors/${professorId}/exercise-media`)).data;
  const map = {};
  (rows || []).forEach((r) => { map[r.exercise_item_id] = r; });
  return map;
}, []);

const refreshAll = useCallback(async () => {
  setLoading(true);

  try {
  const [meData, exercisesData, plansData, usersData] = await Promise.all([
      loadMe(),
      loadExercises(),
      loadPlans(),
      loadUsers(),
    ]);

    setCurrentUser(meData);
    setCategories(exercisesData || []);
    setPlans(plansData || []);
    setUsers(usersData || []);

    // Só professor tem "meus vídeos" — admin já edita o demo_url padrão
    // direto no catálogo. Falha aqui não deve travar o resto do modal.
    if (meData?.role === "professor") {
      loadProfessorExerciseMedia(meData.id)
        .then(setProfessorMediaByItemId)
        .catch(() => {});
    }

    // No layout de coluna unica (mobile/tablet estreito), NAO pre-seleciona —
    // a lista precisa aparecer primeiro (padrao de 2 telas), senao o usuario
    // cai direto no painel de detalhe do primeiro item sem nunca ver a lista.
    // No layout largo (lista + painel lado a lado) a pre-selecao continua
    // valendo, como sempre valeu: evita a tela de "selecione algo" vazia.
    if (exercisesData?.length && !isCollapsedLayoutRef.current) {
      setSelectedCategoryId((prev) => prev || exercisesData[0].id);
    }

    if (plansData?.length && !isCollapsedLayoutRef.current) {
      const listable = computeListablePlans(plansData, meData);
      const defaultPlan = listable[0] || plansData[0];
      setSelectedPlanId((prev) => prev || defaultPlan.id);
    }

    if (usersData?.length && !isCollapsedLayoutRef.current) {
      const visibleInitial = usersData.filter((u) => u.role === "aluno");
      if (visibleInitial.length) {
        setSelectedStudentId((prev) => prev || visibleInitial[0].id);
      }
    }
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao carregar dados.");
  } finally {
    setLoading(false);
  }
}, [loadExercises, loadMe, loadPlans, loadUsers, loadProfessorExerciseMedia, toast]);

useEffect(() => {
  if (!open) return;
  refreshAll();
}, [open, refreshAll]);

useEffect(() => {
  if (!selectedCategoryId || !open) return;

  let active = true;
  (async () => {
    try {
      const items = await loadExerciseItemsByCategory(selectedCategoryId, !showInactiveExercises);
      if (active) setCategoryItems(items || []);
    } catch (err) {
      if (active) toast.error(err.response?.data?.detail || err.message || "Erro ao carregar exercícios.");
    }
  })();

  return () => {
    active = false;
  };
}, [selectedCategoryId, open, showInactiveExercises, loadExerciseItemsByCategory, toast]);

useEffect(() => {
  if (!selectedPlanId || !open) return;

  let active = true;
  (async () => {
    try {
      const plan = await loadPlanDetail(selectedPlanId);
      if (active) setSelectedPlan(plan);
    } catch (err) {
      if (active) toast.error(err.response?.data?.detail || err.message || "Erro ao carregar treino.");
    }
  })();

  return () => {
    active = false;
  };
}, [selectedPlanId, open, loadPlanDetail, toast]);

useEffect(() => {
  if (!selectedStudentId || !open) return;

  let active = true;
  (async () => {
    try {
      const assignments = await loadUserWorkoutPlans(selectedStudentId);
      if (!active) return;
      setStudentAssignments(assignments || []);
      setSelectedStudentAssignmentId((prev) => {
        if (prev && (assignments || []).some((item) => item.id === prev)) return prev;
        return null;
      });
    } catch (err) {
      if (active) toast.error(err.response?.data?.detail || err.message || "Erro ao carregar treinos do aluno.");
    }
  })();

  return () => {
    active = false;
  };
}, [selectedStudentId, open, loadUserWorkoutPlans, toast]);

useEffect(() => {
  if (!openPlanItemModal || !planItemForm.exercise_id) return;

  let active = true;

  (async () => {
    try {
      const items = await loadExerciseItemsByCategory(Number(planItemForm.exercise_id));
      if (active) {
        setPlanExerciseItems(items || []);
      }
    } catch (err) {
      if (active) {
        toast.error(err.response?.data?.detail || err.message || "Erro ao carregar exercícios da categoria.");
      }
    }
  })();

  return () => {
    active = false;
  };
}, [openPlanItemModal, planItemForm.exercise_id, loadExerciseItemsByCategory, toast]);

useEffect(() => {
  if (!open) {
    setTab("catalog");
    setLoading(false);
    setSaving(false);
    setCurrentUser(null);
    setCategories([]);
    setSelectedCategoryId(null);
    setCategoryItems([]);
    setPlanExerciseItems([]);
    setPlans([]);
    setSelectedPlanId(null);
    setSelectedPlan(null);
    setUsers([]);
    setSelectedStudentId(null);
    setStudentAssignments([]);
    setSelectedStudentAssignmentId(null);
    setAssignmentPlanId("");
    setAssignmentItemDraft(null);
    setOpenAssignmentItemModal(false);
    setAssignForm({
      start_date: "",
      end_date: "",
      is_active: true,
    });
    setOpenPlanModal(false);
    setOpenPlanItemModal(false);
    resetExerciseItemEditor();
    resetPlanEditor();
    resetPlanItemEditor();
  }
}, [open, resetExerciseItemEditor, resetPlanEditor, resetPlanItemEditor]);

useEffect(() => {
  if (!open || !currentUser) return;

  if (currentUser.role === "admin") {
    setTab((prev) => (prev === "plans" || prev === "catalog" || prev === "students" ? prev : "catalog"));
  } else {
    setTab((prev) => (prev === "plans" || prev === "students" ? prev : "plans"));
  }
}, [open, currentUser]);

// O Escape do modal externo NAO e tratado aqui: <ModalHeader> ja instala um
// focus trap com handler de Escape no container pai. Havia um segundo
// listener proprio neste ponto, que fazia onClose disparar DUAS vezes — e,
// pior, continuava ativo com um sub-modal aberto, entao fechar o editor de
// exercicio com Escape derrubava o modal inteiro junto com o formulario.
// Ver hooks/modalLayers.js.

// Cada sub-modal e uma CAMADA propria: ganha focus trap, Escape que fecha so
// a si mesmo, e silencia o trap da camada de baixo enquanto estiver aberto.
const exerciseItemModalRef = useRef(null);
const planModalRef = useRef(null);
const planItemModalRef = useRef(null);
const studentAssignmentModalRef = useRef(null);
const assignmentItemModalRef = useRef(null);
const addAnonItemModalRef = useRef(null);

useFocusTrap(exerciseItemModalRef, openExerciseItemModal, resetExerciseItemEditor);
useFocusTrap(planModalRef, openPlanModal, closePlanModal);
useFocusTrap(planItemModalRef, openPlanItemModal, closePlanItemModal);

// Voltar do Android / swipe do iOS fecham UMA camada por vez — ver
// hooks/useHistoryBackClose.js. Antes, voltar com o modal aberto saia do app.
// A camada acompanha apenas `open`. A guarda de geracao de IA vai no
// CALLBACK, nao na condicao: se entrasse na condicao, comecar a gerar
// desregistraria a camada e o cleanup dispararia um history.back() de
// verdade, a cada geracao.
const fecharSeNaoEstiverGerando = useCallback(() => {
  if (!aiGenerating && !aiConfirming) onClose();
}, [aiGenerating, aiConfirming, onClose]);
useHistoryBackClose(open, fecharSeNaoEstiverGerando);
useHistoryBackClose(openExerciseItemModal, resetExerciseItemEditor);
useHistoryBackClose(openPlanModal, closePlanModal);
useHistoryBackClose(openPlanItemModal, closePlanItemModal);
const closeStudentAssignmentModal = useCallback(() => setOpenStudentAssignmentModal(false), []);
useHistoryBackClose(openStudentAssignmentModal, closeStudentAssignmentModal);
useFocusTrap(studentAssignmentModalRef, openStudentAssignmentModal, closeStudentAssignmentModal);
// Os traps de assignmentItem/addAnonItem ficam mais abaixo: os callbacks de
// fechar (closeAssignmentItemEditor / closeAddAnonItem) sao `const` declarados
// so depois deste ponto, e referenciar aqui cairia na temporal dead zone.

// success/error state removed; using direct toast calls instead

const isAdmin = currentUser?.role === "admin";
const isProfessor = currentUser?.role === "professor";

// Treinos que o professor nao "dono" (splits padrao da plataforma,
// professor_id NULL, ou eventualmente o "em branco" se nao foi criado por
// ele) sao somente-leitura pro professor: ele so pode ver os exercicios e
// relacionar o treino com um aluno (aba "Alunos & treinos"). O backend ja
// bloqueia add/editar/excluir/reordenar item com 403 nesse caso (ver
// app/api/workout_plans.py) — este flag so evita mostrar botoes que vao
// falhar, mesmo criterio ja usado em "Editar treino"/"Excluir" do plano.
const canManageSelectedPlan = isAdmin || selectedPlan?.professor_id === currentUser?.id;

const modalDescription = useMemo(() => {
  if (isAdmin) return "Gerencie categorias, exercícios e treinos de todos os professores";
  if (isProfessor) return "Crie treinos padrão e personalize a atribuição por aluno em uma área separada";
  return "Gestão de treinos";
}, [isAdmin, isProfessor]);

const selectedCategory = useMemo(
  () => categories.find((c) => c.id === selectedCategoryId) || null,
  [categories, selectedCategoryId]
);

  // Filtro client-side do catálogo de exercícios (015): busca por nome e pelos
  // rótulos de grupo muscular / equipamento / tipo de movimento. Acento e caixa
  // ignorados. Fecha o débito 015 (docs/07-issues.md) — tela do admin.
  const filteredCategoryItems = useMemo(() => {
    const q = exerciseItemSearch.trim().toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (!q) return categoryItems;
    return categoryItems.filter((item) => {
      const campos = [
        item.name,
        getOptionLabel(MUSCLE_GROUP_OPTIONS, item.muscle_group),
        getOptionLabel(EQUIPMENT_OPTIONS, item.equipment),
        getOptionLabel(MOVEMENT_TYPE_OPTIONS, item.movement_type),
      ];
      const alvo = campos.filter(Boolean).join(" ").toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      return alvo.includes(q);
    });
  }, [categoryItems, exerciseItemSearch]);

  // Limpa a busca ao trocar de categoria
  useEffect(() => {
    setExerciseItemSearch("");
  }, [selectedCategoryId]);

const visiblePlans = useMemo(() => {
  const list = isAdmin
    ? plans
    : isProfessor
    ? plans.filter((p) => p.professor_id === currentUser?.id || p.is_blank || p.is_standard)
    : [];
  // Treinos proprios (do professor, incluindo o "em branco") primeiro, splits
  // padrao da plataforma depois — dentro de cada grupo, ordem alfabetica.
  return [...list].sort((a, b) => {
    const standardDiff = (a.is_standard ? 1 : 0) - (b.is_standard ? 1 : 0);
    if (standardDiff !== 0) return standardDiff;
    return (a.title || "").localeCompare(b.title || "", "pt-BR", { sensitivity: "base" });
  });
}, [plans, isAdmin, isProfessor, currentUser]);

// O treino "em branco" e um template do sistema (nao pode ser excluido, ver
// isBlankPlan/handleDeletePlan) e aparece pra TODOS os professores via o "||
// p.is_blank" do visiblePlans acima — misturado em ordem alfabetica na lista,
// fica um item fora do padrao dos outros nomes e sem serventia de navegacao
// pro professor. Admin continua vendo (gerencia o catalogo todo).
const listablePlans = useMemo(
  () => (isAdmin ? visiblePlans : visiblePlans.filter((p) => !p.is_blank)),
  [visiblePlans, isAdmin]
);

const filteredPlans = useMemo(() => {
  const q = planQuery.trim().toLowerCase();
  if (!q) return listablePlans;
  return listablePlans.filter((p) => {
    const title = (p.title || "").toLowerCase();
    const prof = (
      p.professor?.name || professorsById.get(p.professor_id)?.name || ""
    ).toLowerCase();
    return (isAdmin && p.is_blank) || title.includes(q) || prof.includes(q) || String(p.id).includes(q);
  });
}, [planQuery, listablePlans, professorsById, isAdmin]);

const selectedAssignmentPlan = useMemo(
  () => visiblePlans.find((p) => String(p.id) === String(assignmentPlanId)) || null,
  [visiblePlans, assignmentPlanId]
);

const planPickerResults = useMemo(() => {
  const q = planPickerQuery.trim().toLowerCase();
  if (!q) return visiblePlans;
  return visiblePlans.filter((p) => (p.title || "").toLowerCase().includes(q));
}, [visiblePlans, planPickerQuery]);

// Fecha o combobox ao clicar fora dele.
useEffect(() => {
  if (!planPickerOpen) return;
  const onDocClick = (e) => {
    if (planPickerRef.current && !planPickerRef.current.contains(e.target)) {
      setPlanPickerOpen(false);
      setPlanPickerQuery("");
      setPlanPickerActiveIndex(-1);
    }
  };
  document.addEventListener("mousedown", onDocClick);
  return () => document.removeEventListener("mousedown", onDocClick);
}, [planPickerOpen]);

const visibleStudents = useMemo(() => {
  if (isAdmin || isProfessor) {
    const alunos = users.filter((u) => u.role === "aluno");
    if (currentUser && isProfessor) {
      const selfEntry = { ...currentUser, _isSelf: true };
      return [...alunos, selfEntry].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
    }
    return alunos;
  }
  return [];
}, [users, isAdmin, isProfessor, currentUser]);

const filteredStudents = useMemo(() => {
  const q = studentQuery.trim().toLowerCase();
  if (!q) return visibleStudents;
  return visibleStudents.filter((u) => {
    const name = (u.name || "").toLowerCase();
    const email = (u.email || "").toLowerCase();
    const prof = (
      u.professor?.name || professorsById.get(u.professor_id)?.name || ""
    ).toLowerCase();
    return name.includes(q) || email.includes(q) || prof.includes(q);
  });
}, [studentQuery, visibleStudents, professorsById]);

const selectedStudent = useMemo(
  () => visibleStudents.find((user) => user.id === selectedStudentId) || null,
  [selectedStudentId, visibleStudents]
);

useEffect(() => {
  setHasRationale(false);
  if (!selectedStudent?.id) return;
  api.get(`/ai/student-workout-rationale/${selectedStudent.id}`)
    .then(res => { if (res.data.rationale) setHasRationale(true); })
    .catch(() => {});
}, [selectedStudent?.id]);

useEffect(() => {
  if (!visibleStudents.length) {
    setSelectedStudentId(null);
    return;
  }

  setSelectedStudentId((prev) => {
    if (prev && visibleStudents.some((student) => student.id === prev)) return prev;
    // No mobile (coluna unica), nao auto-seleciona o primeiro aluno -- a lista
    // precisa aparecer antes (padrao de 2 telas de drill-down); so limpa selecao invalida.
    return isCollapsedLayoutRef.current ? null : visibleStudents[0].id;
  });
}, [visibleStudents]);

const selectedStudentAssignment = useMemo(
  () => studentAssignments.find((item) => item.id === selectedStudentAssignmentId) || null,
  [selectedStudentAssignmentId, studentAssignments]
);

const planCategoryItems = useMemo(() => {
  if (!planItemForm.exercise_id) return [];
  return planExerciseItems;
}, [planExerciseItems, planItemForm.exercise_id]);

const runAction = useCallback(
  async (action, successMessage) => {
    resetFeedback();
    setSaving(true);

    try {
      await action();
      if (successMessage) toast.success(successMessage);
      await refreshAll();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erro ao processar ação.");
      return false;
    } finally {
      setSaving(false);
    }
  },
  [refreshAll, resetFeedback, toast]
);

const handleSaveExerciseItem = useCallback(
  async (e) => {
    e.preventDefault();
    if (!selectedCategoryId) return;

    const payload = {
      ...exerciseItemForm,
      exercise_id: selectedCategoryId,
    };

    const demo = String(exerciseItemForm.demo_url || "").trim();
    const hasUrl = demo.length > 0;
    const hasLocalUrl = hasUrl && demo.startsWith('uploads/media');
    // Prioridade: URL externa > arquivo. Se a URL atual é local, permitimos substituir pelo arquivo.
    const prioritizeUrl = hasUrl && !hasLocalUrl;

    if (editingExerciseItemId) {
      const ok = await runAction(
        () =>
          api.patch(`/exercise-items/${editingExerciseItemId}`, payload),
        "Exercício atualizado com sucesso."
      );

      if (ok && !prioritizeUrl && exerciseItemFile) {
        const fd = new FormData();
        fd.append("file", exerciseItemFile);
        try {
          const savedMedia = (await api.post(`/exercise-items/${editingExerciseItemId}/demo-media`, fd)).data;
          // Vídeo é comprimido em segundo plano (app/jobs/video_compression_job.py)
          // — já fica tocável na hora, só troca pela versão otimizada depois.
          if (savedMedia?.demo_status === "processing") {
            toast.success("Vídeo enviado! Já pode ser usado - a versão otimizada fica pronta em instantes.");
          } else {
            toast.success("Mídia de demonstração enviada.");
          }
          await refreshAll();
        } catch (err) {
          toast.error(err.response?.data?.detail || err.message || "Falha ao enviar mídia de demonstração.");
        }
      }

      resetExerciseItemEditor();
      return;
    }

    // Criação: precisamos do ID para enviar a mídia
    setSaving(true);
    try {
      const created = (await api.post("/exercise-items", payload)).data;
      toast.success("Exercício criado com sucesso.");

      if (!prioritizeUrl && exerciseItemFile && created?.id) {
        const fd = new FormData();
        fd.append("file", exerciseItemFile);
        try {
          const savedMedia = (await api.post(`/exercise-items/${created.id}/demo-media`, fd)).data;
          if (savedMedia?.demo_status === "processing") {
            toast.success("Vídeo enviado! Já pode ser usado - a versão otimizada fica pronta em instantes.");
          } else {
            toast.success("Mídia de demonstração enviada.");
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || err.message || "Falha ao enviar mídia de demonstração.");
        }
      }

      await refreshAll();
      resetExerciseItemEditor();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erro ao criar exercício.");
    } finally {
      setSaving(false);
    }
  },
  [
    editingExerciseItemId,
    exerciseItemForm,
    exerciseItemFile,
    refreshAll,
    resetExerciseItemEditor,
    runAction,
    selectedCategoryId,
    toast,
    setSaving,
  ]
);

const handleDeleteExerciseItem = useCallback(
  async (itemId) => {
    setConfirmMessage("Deseja realmente remover este exercício?");
    setConfirmLabel("Remover exercício");
    confirmActionRef.current = async () => {
      await runAction(
        () =>
          api.delete(`/exercise-items/${itemId}`),
        "Exercício removido com sucesso."
      );
      if (editingExerciseItemId === itemId) {
        resetExerciseItemEditor();
      }
    };
    setShowConfirm(true);
  },
  [editingExerciseItemId, resetExerciseItemEditor, runAction]
);

const startEditExerciseItem = useCallback(
  (item) => {
    resetFeedback();
    setEditingExerciseItemId(item.id);
    setExerciseItemForm({
      name: item.name || "",
      muscle_group: item.muscle_group || "",
      equipment: item.equipment || "",
      movement_type: item.movement_type || "",
      demo_url: item.demo_url || "",
      demo_status: item.demo_status ?? null,
      notes: item.notes || "",
      is_active: !!item.is_active,
    });
    setExerciseItemFile(null);
    setOpenExerciseItemModal(true);
  },
  [resetFeedback]
);

const startCreateExerciseItem = useCallback(() => {
  resetFeedback();
  setEditingExerciseItemId(null);
  setExerciseItemForm(getEmptyExerciseItemForm());
  setExerciseItemFile(null);
  setOpenExerciseItemModal(true);
}, [resetFeedback]);

const askRemoveExerciseMedia = useCallback(() => {
  if (!editingExerciseItemId) return;
  setConfirmMessage("Remover mídia de demonstração deste exercício?");
  setConfirmLabel("Remover mídia");
  confirmActionRef.current = async () => {
    try {
      await api.delete(`/exercise-items/${editingExerciseItemId}/demo-media`);
      toast.success("Mídia removida.");
      setExerciseItemForm((prev) => ({ ...prev, demo_url: "" }));
      setExerciseItemFile(null);
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Falha ao remover mídia.");
    }
  };
  setShowConfirm(true);
}, [editingExerciseItemId, refreshAll, toast]);

const handleSavePlan = useCallback(
  async (e) => {
    e.preventDefault();

    if (editingPlanId) {
      const ok = await runAction(
        () =>
          api.patch(`/workout-plans/${editingPlanId}`, {
              title: planForm.title,
              description: planForm.description,
              is_active: planForm.is_active,
              is_blank: !!planForm.is_blank,
            }),
        "Treino atualizado com sucesso."
      );

      if (ok) {
        // Nada local: backend persiste is_blank
        resetPlanEditor();
        setOpenPlanModal(false);
      }
    } else {
      // Criação: agora persiste no backend
      setSaving(true);
      try {
        await api.post("/workout-plans", {
            title: planForm.title,
            description: planForm.description,
            is_active: planForm.is_active,
            is_blank: !!planForm.is_blank,
            items: [],
          });
        toast.success("Treino criado com sucesso.");
        await refreshAll();
        resetPlanEditor();
        setOpenPlanModal(false);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || "Erro ao criar treino.");
      } finally {
        setSaving(false);
      }
    }
  },
  [editingPlanId, planForm, resetPlanEditor, runAction, toast, refreshAll]
);

const handleDeletePlan = useCallback(
  async (planId) => {
    const plan = plans.find((p) => p.id === planId);
    const planTitle = plan?.title ? `"${plan.title}"` : "este treino";
    setConfirmMessage(
      `Excluir ${planTitle}?\n\nIsso removerá permanentemente o treino e os exercícios dele. Só é possível excluir um treino que não esteja com nenhum aluno.`
    );
    setConfirmLabel("Excluir treino");
    confirmActionRef.current = async () => {
      const ok = await runAction(
        () =>
          api.delete(`/workout-plans/${planId}`),
        "Treino excluído com sucesso."
      );
      // Recusado (ex.: treino em uso por alunos): mantém o treino aberto
      if (!ok) return;
      if (selectedPlanId === planId) {
        setSelectedPlanId(null);
        setSelectedPlan(null);
      }
      if (editingPlanId === planId) {
        resetPlanEditor();
      }
    };
    setShowConfirm(true);
  },
  [editingPlanId, plans, resetPlanEditor, runAction, selectedPlanId]
);

const handlePlanCategoryChange = useCallback(
  async (categoryId) => {
    setPlanItemForm((prev) => ({
      ...prev,
      exercise_id: categoryId,
      exercise_item_id: "",
    }));

    if (!categoryId) {
      setPlanExerciseItems([]);
      return;
    }

    try {
      const items = await loadExerciseItemsByCategory(Number(categoryId));
      setPlanExerciseItems(items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erro ao carregar exercícios da categoria.");
    }
  },
    [loadExerciseItemsByCategory, toast]
);

const handleSavePlanItem = useCallback(
  async (e) => {
    e.preventDefault();
    if (!selectedPlanId) return;

    const payload = {
      exercise_id: Number(planItemForm.exercise_id),
      exercise_item_id: Number(planItemForm.exercise_item_id),
      order_index: Number(planItemForm.order_index || 0),
      sets_planned: planItemForm.sets_planned ? Number(planItemForm.sets_planned) : null,
      reps_planned: planItemForm.reps_planned || null,
      duration_planned: planItemForm.duration_planned ? Number(planItemForm.duration_planned) : null,
      weight_planned: planItemForm.weight_planned ? Number(planItemForm.weight_planned) : null,
      rest_seconds: planItemForm.rest_seconds || null,
      notes: planItemForm.notes || null,
    };

    if (editingPlanItemId) {
      const ok = await runAction(
        () =>
          api.patch(`/workout-plans/items/${editingPlanItemId}`, payload),
        "Item do treino atualizado."
      );

      if (ok) {
        await refreshSelectedPlan(selectedPlanId);
        resetPlanItemEditor();
        setOpenPlanItemModal(false);
      }
    } else {
      const ok = await runAction(
        () =>
          api.post(`/workout-plans/${selectedPlanId}/items`, payload),
        "Item adicionado ao treino."
      );

      if (ok) {
        await refreshSelectedPlan(selectedPlanId);
        resetPlanItemEditor();
        setOpenPlanItemModal(false);
      }
    }
  },
  [
    editingPlanItemId,
    planItemForm,
    resetPlanItemEditor,
    runAction,
    selectedPlanId,
    refreshSelectedPlan,
  ]
);

const handleDeletePlanItem = useCallback(
  async (itemId) => {
    setConfirmMessage("Deseja realmente remover este item do treino?");
    setConfirmLabel("Remover item");
    confirmActionRef.current = async () => {
      const ok = await runAction(
        () =>
          api.delete(`/workout-plans/items/${itemId}`),
        "Item removido com sucesso."
      );
      if (ok) {
        await refreshSelectedPlan(selectedPlanId);
        if (editingPlanItemId === itemId) {
          resetPlanItemEditor();
        }
      }
    };
    setShowConfirm(true);
  },
  [
    editingPlanItemId,
    resetPlanItemEditor,
    runAction,
    selectedPlanId,
    refreshSelectedPlan,
  ]
);

// Bi-set/superset/circuito — seleção múltipla + agrupar/desagrupar
// (WorkoutAdminModal, template do treino). Ver group_id em
// app/models/exercise.py e /workout-plans/{plan_id}/items/group.
const toggleGroupSelectItem = useCallback((itemId) => {
  setGroupSelectedIds((prev) =>
    prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
  );
}, []);

const handleCancelGroupSelect = useCallback(() => {
  setGroupSelectMode(false);
  setGroupSelectedIds([]);
}, []);

const handleCreateGroup = useCallback(async () => {
  if (groupSelectedIds.length < 2) {
    toast.error("Selecione ao menos 2 exercícios para agrupar.");
    return;
  }
  const ok = await runAction(
    () => api.post(`/workout-plans/${selectedPlanId}/items/group`, { item_ids: groupSelectedIds }),
    "Exercícios agrupados."
  );
  if (ok) {
    await refreshSelectedPlan(selectedPlanId);
    setGroupSelectedIds([]);
    setGroupSelectMode(false);
  }
}, [groupSelectedIds, runAction, selectedPlanId, refreshSelectedPlan, toast]);

// Grupos existentes que a seleção atual vai desfazer parcialmente — achado
// #1 da auditoria UX. Memoizado porque é lido tanto no aviso inline quanto
// no clique do botão "Agrupar".
const groupConflicts = useMemo(
  () => findGroupConflicts(selectedPlan?.items || [], groupSelectedIds),
  [selectedPlan, groupSelectedIds]
);

// Séries divergentes entre os itens selecionados — achado #1 da revisão UX
// (06/09/2026). Mesmo padrão de memoização de groupConflicts.
const groupSetsMismatch = useMemo(
  () => findSetsMismatch(selectedPlan?.items || [], groupSelectedIds),
  [selectedPlan, groupSelectedIds]
);

// Rótulo dinâmico do botão "Agrupar" — achado "Later" da auditoria UX:
// em vez de um "Agrupar" genérico, mostra quantos itens estão selecionados
// e qual tipo de agrupamento vai resultar (bi-set/superset/circuito),
// reutilizando a mesma lógica de groupLabel().
const groupSelectionType = useMemo(() => {
  if (groupSelectedIds.length < 2) return null;
  const selectedItems = (selectedPlan?.items || []).filter((it) => groupSelectedIds.includes(it.id));
  return groupLabel(selectedItems);
}, [selectedPlan, groupSelectedIds]);

// Clique do botão "Agrupar": se a seleção vai desfazer um grupo já
// existente, avisa e pede confirmação antes de chamar handleCreateGroup.
// Sem conflito, agrupa direto (comportamento inalterado).
const handleCreateGroupClick = useCallback(() => {
  if (groupConflicts.length === 0 && !groupSetsMismatch) {
    handleCreateGroup();
    return;
  }
  const messages = [];
  if (groupConflicts.length > 0) {
    const desc = groupConflicts.map((c) => c.names.join(" + ")).join("; ");
    messages.push(`Isso vai desfazer o grupo com ${desc}, porque nem todos os exercícios dele estão selecionados.`);
  }
  if (groupSetsMismatch) {
    // Achado #1 da revisão UX: sem esse aviso, o professor só descobre que
    // um exercício "sai do rodízio" antes dos outros quando o aluno já
    // está no meio do treino (ver GroupSetsTracker.jsx).
    const desc = groupSetsMismatch.map((s) => `${s.name} (${s.sets}x)`).join(", ");
    messages.push(`Os exercícios selecionados têm números de série diferentes - ${desc}. O de menos séries vai terminar antes e sair do rodízio durante o treino.`);
  }
  messages.push("Deseja continuar?");
  setConfirmMessage(messages.join(" "));
  setConfirmLabel("Agrupar mesmo assim");
  confirmActionRef.current = handleCreateGroup;
  setShowConfirm(true);
}, [groupConflicts, groupSetsMismatch, handleCreateGroup]);

const handleUngroupBlock = useCallback(
  async (groupId) => {
    const ok = await runAction(
      () => api.delete(`/workout-plans/${selectedPlanId}/items/group/${groupId}`),
      "Grupo desfeito - exercícios voltaram a ser avulsos."
    );
    if (ok) {
      await refreshSelectedPlan(selectedPlanId);
    }
  },
  [runAction, selectedPlanId, refreshSelectedPlan]
);

// Move um BLOCO inteiro (item avulso ou grupo contíguo) uma posição pra
// cima/baixo, trocando de lugar com o bloco vizinho — nunca intercala um
// item de fora no meio de um grupo (o que quebraria a contiguidade que o
// backend garante ao agrupar). Generaliza handleMovePlanItem (que só move
// 1 item) pra mover N itens de uma vez quando o bloco é um grupo.
const handleMoveBlock = useCallback(
  async (block, dir) => {
    if (!selectedPlan || !Array.isArray(selectedPlan.items) || !selectedPlan.items.length) return;
    const sorted = [...selectedPlan.items].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    const blocks = buildItemBlocks(sorted);
    const curIdx = blocks.findIndex((b) =>
      b.kind === "group" && block.kind === "group"
        ? b.groupId === block.groupId
        : b.kind === "item" && block.kind === "item" && b.item.id === block.item.id
    );
    if (curIdx < 0) return;
    const tgtIdx = curIdx + dir;
    if (tgtIdx < 0 || tgtIdx >= blocks.length) return;

    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(curIdx, 1);
    newBlocks.splice(tgtIdx, 0, moved);

    const flatNewOrder = newBlocks.flatMap((b) => (b.kind === "group" ? b.items : [b.item]));
    // Manda a lista INTEIRA renumerada, não só os itens cujo order_index
    // (na visão do frontend) mudou — ver nota grande em
    // handleMoveAssignmentBlock abaixo pro caso concreto (agrupar + mover
    // logo em seguida) em que só reescrever o "diff" deixa item(ns) com
    // order_index bruto desatualizado, colidindo com o valor novo de outro
    // item e quebrando a adjacência de um grupo (ver docs/07-issues.md).
    const changes = flatNewOrder.map((it, idx) => ({ id: it.id, order_index: idx }));

    setSaving(true);
    try {
      // Lote atômico (endpoint dedicado) em vez de 1 PATCH por item em
      // sequência — antes, uma falha de rede no meio do loop podia deixar
      // o plano com parte dos itens reordenados e parte não (ver R248/
      // docs/07-issues.md).
      await api.patch(`/workout-plans/items/reorder`, { items: changes });
      await refreshSelectedPlan(selectedPlanId);
      toast.success("Ordem atualizada.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erro ao reordenar.");
    } finally {
      setSaving(false);
    }
  },
  [refreshSelectedPlan, selectedPlan, selectedPlanId, toast]
);

const handleAssignPlanToStudent = useCallback(async () => {
  if (!selectedStudentId || !assignmentPlanId) {
    toast.error("Selecione um aluno e um treino padrão.");
    return;
  }

  resetFeedback();
  setSaving(true);

  try {
    await api.post(`/user-workout-plans/plans/${assignmentPlanId}/assign`, {
        user_ids: [selectedStudentId],
        is_active: assignForm.is_active,
        day_of_week: assignForm.day_of_week === "" ? null : Number(assignForm.day_of_week),
        custom_name: assignForm.custom_name.trim() || null,
      });

    const assignments = await loadUserWorkoutPlans(selectedStudentId);
    setStudentAssignments(assignments || []);
    setSelectedStudentAssignmentId(null);
    setAssignmentPlanId("");
    toast.success("Treino relacionado ao aluno com sucesso.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao relacionar treino ao aluno.");
  } finally {
    setSaving(false);
  }
}, [assignmentPlanId, assignForm, loadUserWorkoutPlans, resetFeedback, selectedStudentId, toast]);

const handleRemoveStudentAssignment = useCallback(async (assignmentId) => {
  setConfirmMessage("Deseja remover este treino do aluno?");
  setConfirmLabel("Remover");
  confirmActionRef.current = async () => {
    resetFeedback();
    setSaving(true);
    try {
      await api.delete(`/user-workout-plans/${assignmentId}`);
      const assignments = await loadUserWorkoutPlans(selectedStudentId);
      setStudentAssignments(assignments || []);
      setSelectedStudentAssignmentId((prev) =>
        prev === assignmentId ? null : prev
      );
      toast.success("Treino removido do aluno.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erro ao remover treino do aluno.");
    } finally {
      setSaving(false);
    }
  };
  setShowConfirm(true);
}, [loadUserWorkoutPlans, resetFeedback, selectedStudentId, toast]);

// O treino do aluno é uma cópia do treino padrão no momento em que foi
// relacionado (R286): mudar o padrão não afeta o aluno. Sincronizar é a única
// forma de levar pro aluno exercícios incluídos no padrão depois, então pede
// confirmação explicando o que entra e o que não muda.
const handleSyncStudentAssignment = useCallback((assignmentId) => {
  setConfirmTitle("Sincronizar com o treino padrão?");
  setConfirmVariant("success");
  setConfirmLabel("Sincronizar");
  setConfirmMessage(
    <div className={styles.syncConfirm}>
      <p>
        Traz para o treino <strong>deste aluno</strong> os exercícios que foram incluídos no treino padrão
        depois que ele foi relacionado ao aluno.
      </p>
      <ul>
        <li>Os exercícios que o aluno já tem não mudam: séries, repetições, carga e ajustes feitos para ele continuam iguais.</li>
        <li>Exercícios que você removeu do treino deste aluno não voltam.</li>
        <li>Exercícios removidos do treino padrão continuam no treino do aluno.</li>
        <li>Os outros alunos que usam este treino padrão não são afetados.</li>
      </ul>
      <p className={styles.syncConfirmWarn}>
        Para desfazer, remova os exercícios incluídos direto no treino do aluno.
      </p>
    </div>
  );
  confirmActionRef.current = async () => {
    resetFeedback();
    setSaving(true);
    try {
      const before = studentAssignments.find((item) => item.id === assignmentId);
      const updatedAssignment = (await api.post(`/user-workout-plans/${assignmentId}/sync`)).data;

      const assignments = studentAssignments.map((item) =>
        item.id === updatedAssignment.id ? updatedAssignment : item
      );
      setStudentAssignments(assignments);
      setSelectedStudentAssignmentId(updatedAssignment.id);
      const added = (updatedAssignment.items?.length || 0) - (before?.items?.length || 0);
      toast.success(
        added > 0
          ? `${added} ${added === 1 ? "exercício incluído" : "exercícios incluídos"} no treino do aluno.`
          : "Nada novo no treino padrão: o treino do aluno continua igual."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erro ao sincronizar treino do aluno.");
    } finally {
      setSaving(false);
    }
  };
  setShowConfirm(true);
}, [resetFeedback, studentAssignments, toast]);

// Preenche o form de meta com dados do vínculo selecionado
useEffect(() => {
  if (!selectedStudentAssignmentId) {
    setAssignmentMetaForm({ day_of_week: "" });
    return;
  }
  const a = (studentAssignments || []).find((x) => x.id === selectedStudentAssignmentId);
  const value = a && a.day_of_week !== null && a.day_of_week !== undefined ? String(a.day_of_week) : "";
  setAssignmentMetaForm({ day_of_week: value, custom_name: a?.custom_name || "" });
}, [selectedStudentAssignmentId, studentAssignments]);

const handleUpdateAssignmentMeta = useCallback(async () => {
  if (!selectedStudentAssignmentId) return;
  resetFeedback();
  setSaving(true);
  try {
    const updatedAssignment = (await api.patch(`/user-workout-plans/${selectedStudentAssignmentId}`, {
        day_of_week: assignmentMetaForm.day_of_week === "" ? null : Number(assignmentMetaForm.day_of_week),
        custom_name: assignmentMetaForm.custom_name.trim() || null,
      })).data;
    // Update only this assignment to preserve any unsaved reorder state on others
    setStudentAssignments((prev) => prev.map((a) => (a.id === updatedAssignment.id ? updatedAssignment : a)));
    toast.success("Preferências atualizadas.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao atualizar preferência de dia.");
  } finally {
    setSaving(false);
  }
}, [assignmentMetaForm, resetFeedback, selectedStudentAssignmentId, toast]);

const openAssignmentItemEditor = useCallback((item) => {
  setAssignmentItemDraft({
    id: item.id,
    exercise_item_id: item.exercise_item_id,
    exercise_item_name: item.exercise_item?.name || "",
    movement_type: item.exercise_item?.movement_type || "",
    sets_planned: item.sets_planned ?? "",
    reps_planned: item.reps_planned ?? "",
    reps_per_set: item.sets_planned ? parseRepsPerSet(item.reps_planned ?? "", Number(item.sets_planned)) : [],
    duration_planned: item.duration_planned ?? "",
    weight_planned: item.weight_planned ?? "",
    rest_seconds: item.rest_seconds ?? "",
    rest_per_set: item.sets_planned ? parseRestPerSet(item.rest_seconds ?? "", Number(item.sets_planned)) : [],
    notes: item.notes ?? "",
    has_custom_demo: !!item.has_custom_demo,
    // "student" | "professor" | null — ver serialize_effective_assignment.
    // null com has_custom_demo=true nunca deveria acontecer, mas o badge
    // trata como "personalizado" genérico nesse caso (fallback defensivo).
    demo_source: item.demo_source ?? null,
    demo_status: item.demo_status ?? null,
    demo_url: item.exercise_item?.demo_url || "",
    // Só pré-preenche com a URL atual quando ela É o override deste aluno -
    // se for a demo PADRÃO da plataforma (has_custom_demo=false), deixa vazio:
    // mostrar a URL padrão aqui parece "já configurado" e confunde o
    // professor a achar que precisa apagar antes de colar a dele.
    demo_url_input: item.has_custom_demo ? (item.exercise_item?.demo_url || "") : "",
  });
  setOpenAssignmentItemModal(true);
}, []);

const closeAssignmentItemEditor = useCallback(() => {
  setAssignmentItemDraft(null);
  setOpenAssignmentItemModal(false);
}, []);

const [anonItemForm, setAnonItemForm] = useState({
  exercise_id: "",
  exercise_item_id: "",
  order_index: 0,
  sets_planned: "",
  reps_planned: "",
  reps_per_set: [],
  duration_planned: "",
  weight_planned: "",
  rest_seconds: "",
  rest_per_set: [],
  notes: "",
});

const openAddAnonItem = useCallback(() => {
  resetFeedback();
  setAnonItemForm({
    exercise_id: "",
    exercise_item_id: "",
    order_index: Math.max(0, ...(selectedStudentAssignment?.items?.map(i => i.order_index ?? 0) || [])) + 1,
    sets_planned: "",
    reps_planned: "",
    reps_per_set: [],
    duration_planned: "",
    weight_planned: "",
    rest_seconds: "",
    rest_per_set: [],
    notes: "",
  });
  setAnonExerciseItems([]);
  setOpenAddAnonItemModal(true);
}, [resetFeedback, selectedStudentAssignment]);

const closeAddAnonItem = useCallback(() => {
  setOpenAddAnonItemModal(false);
  setAnonExerciseItems([]);
}, []);

// Camadas declaradas aqui (e nao junto das outras, la em cima) porque
// dependem dos dois callbacks acima — ver comentario no bloco de traps.
useFocusTrap(assignmentItemModalRef, openAssignmentItemModal, closeAssignmentItemEditor);
useFocusTrap(addAnonItemModalRef, openAddAnonItemModal, closeAddAnonItem);
useHistoryBackClose(openAssignmentItemModal, closeAssignmentItemEditor);
useHistoryBackClose(openAddAnonItemModal, closeAddAnonItem);

const handleAnonCategoryChange = useCallback(async (categoryId) => {
  setAnonItemForm((prev) => ({ ...prev, exercise_id: categoryId, exercise_item_id: "" }));
  if (!categoryId) {
    setAnonExerciseItems([]);
    return;
  }
  try {
    const items = await loadExerciseItemsByCategory(Number(categoryId));
    setAnonExerciseItems(items || []);
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao carregar exercícios da categoria.");
  }
}, [loadExerciseItemsByCategory, toast]);

const handleSaveAnonAssignmentItem = useCallback(async (e) => {
  e.preventDefault();
  if (!selectedStudentAssignmentId || !anonItemForm.exercise_id || !anonItemForm.exercise_item_id) return;
  resetFeedback();
  setSaving(true);
  try {
    const payload = {
      exercise_id: Number(anonItemForm.exercise_id),
      exercise_item_id: Number(anonItemForm.exercise_item_id),
      order_index: Number(anonItemForm.order_index || 0),
      sets_planned: anonItemForm.sets_planned ? Number(anonItemForm.sets_planned) : null,
      reps_planned: anonItemForm.reps_planned || null,
      duration_planned: anonItemForm.duration_planned ? Number(anonItemForm.duration_planned) : null,
      weight_planned: anonItemForm.weight_planned ? Number(anonItemForm.weight_planned) : null,
      rest_seconds: anonItemForm.rest_seconds || null,
      notes: anonItemForm.notes || null,
    };

    const updated = (await api.post(`/user-workout-plans/${selectedStudentAssignmentId}/items`, payload)).data;
    setStudentAssignments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    toast.success("Exercício avulso adicionado ao aluno.");
    closeAddAnonItem();
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao adicionar exercício avulso.");
  } finally {
    setSaving(false);
  }
}, [anonItemForm, closeAddAnonItem, resetFeedback, selectedStudentAssignmentId, toast]);

const handleSaveAssignmentItem = useCallback(async (e) => {
  e.preventDefault();
  if (!selectedStudentAssignmentId || !assignmentItemDraft?.id) return;

  resetFeedback();
  setSaving(true);
  try {
    const payload = {
      sets_planned: assignmentItemDraft.sets_planned ? Number(assignmentItemDraft.sets_planned) : null,
      reps_planned: assignmentItemDraft.reps_planned || null,
      duration_planned: assignmentItemDraft.duration_planned ? Number(assignmentItemDraft.duration_planned) : null,
      weight_planned: assignmentItemDraft.weight_planned ? Number(assignmentItemDraft.weight_planned) : null,
      rest_seconds: assignmentItemDraft.rest_seconds || null,
      notes: assignmentItemDraft.notes || null,
    };

    const updatedAssignment = (await api.patch(
      `/user-workout-plans/${selectedStudentAssignmentId}/items/${assignmentItemDraft.id}`,
      payload
    )).data;

    const assignments = studentAssignments.map((item) =>
      item.id === updatedAssignment.id ? updatedAssignment : item
    );
    setStudentAssignments(assignments);
    toast.success("Treino do aluno atualizado com sucesso.");
    closeAssignmentItemEditor();
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao salvar personalização do aluno.");
  } finally {
    setSaving(false);
  }
}, [assignmentItemDraft, closeAssignmentItemEditor, resetFeedback, selectedStudentAssignmentId, studentAssignments, toast]);

const [uploadingDemo, setUploadingDemo] = useState(false);

// Vídeo custom é por (aluno, exercício) - não pelo vínculo de plano - por
// isso recarrega TODAS as atribuições do aluno após subir/remover, em vez
// de só atualizar o item local: qualquer outro treino do aluno que use o
// mesmo exercício também deve refletir o novo vídeo (ou a volta ao padrão).
const handleUploadStudentDemo = useCallback(async (file) => {
  if (!selectedStudentId || !assignmentItemDraft?.exercise_item_id || !file) return;
  const sizeError = checkMediaFileSize(file);
  if (sizeError) {
    toast.error(sizeError);
    return;
  }
  setUploadingDemo(true);
  try {
    const form = new FormData();
    form.append("file", file);
    const saved = (await api.post(
      `/users/${selectedStudentId}/exercise-media/${assignmentItemDraft.exercise_item_id}`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    )).data;
    const assignments = await loadUserWorkoutPlans(selectedStudentId);
    setStudentAssignments(assignments);
    // Vídeo do aluno é sempre o tier vencedor (1º na cascata) — não precisa
    // reler do backend pra saber a origem, já sabemos que virou "student".
    setAssignmentItemDraft((prev) => (prev ? { ...prev, has_custom_demo: true, demo_source: "student", demo_url: saved.demo_url, demo_status: saved.demo_status } : prev));
    // Vídeo é aceito na hora e comprimido em segundo plano (ver
    // app/jobs/video_compression_job.py) — avisa que já está tocável, mas
    // ainda vai trocar pela versão final em alguns instantes.
    if (saved.demo_status === "processing") {
      toast.success("Vídeo enviado! Já pode ser usado - a versão otimizada fica pronta em instantes.");
    } else {
      toast.success("Vídeo personalizado salvo para este aluno.");
    }
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao enviar vídeo personalizado.");
  } finally {
    setUploadingDemo(false);
  }
}, [assignmentItemDraft, loadUserWorkoutPlans, selectedStudentId, toast]);

// URL colada tem prioridade sobre arquivo, mesma regra do catálogo padrão
// (ver ExerciseItem em WorkoutAdminModal::handleSaveExerciseItem) - só que
// aqui a URL manda sozinha, num campo dedicado.
const handleSaveStudentDemoUrl = useCallback(async () => {
  if (!selectedStudentId || !assignmentItemDraft?.exercise_item_id) return;
  const url = String(assignmentItemDraft.demo_url_input || "").trim();
  if (!url) return;
  setUploadingDemo(true);
  try {
    const saved = (await api.post(
      `/users/${selectedStudentId}/exercise-media/${assignmentItemDraft.exercise_item_id}`,
      new URLSearchParams({ demo_url: url }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    )).data;
    const assignments = await loadUserWorkoutPlans(selectedStudentId);
    setStudentAssignments(assignments);
    // URL externa não passa pelo pipeline de compressão (não é upload de
    // arquivo) — demo_status volta a "ready" nesse fluxo.
    setAssignmentItemDraft((prev) => (prev ? { ...prev, has_custom_demo: true, demo_source: "student", demo_status: saved.demo_status ?? "ready", demo_url: saved.demo_url, demo_url_input: saved.demo_url } : prev));
    toast.success("Vídeo personalizado salvo para este aluno.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao salvar URL de demonstração.");
  } finally {
    setUploadingDemo(false);
  }
}, [assignmentItemDraft, loadUserWorkoutPlans, selectedStudentId, toast]);

const handleRemoveStudentDemo = useCallback(async () => {
  if (!selectedStudentId || !assignmentItemDraft?.exercise_item_id) return;
  setUploadingDemo(true);
  try {
    await api.delete(`/users/${selectedStudentId}/exercise-media/${assignmentItemDraft.exercise_item_id}`);
    const assignments = await loadUserWorkoutPlans(selectedStudentId);
    setStudentAssignments(assignments);
    // Ao remover o override do aluno, pode sobrar o vídeo do PROFESSOR dele
    // (2º tier da cascata) — relê do backend em vez de assumir "sem vídeo",
    // pra não mostrar "padrão da plataforma" quando na verdade ainda tem um
    // vídeo do professor valendo.
    const refreshedAssignment = assignments.find((a) => a.id === selectedStudentAssignmentId);
    const refreshedItem = refreshedAssignment?.items?.find((it) => it.id === assignmentItemDraft.id);
    setAssignmentItemDraft((prev) => (prev
      ? {
          ...prev,
          has_custom_demo: !!refreshedItem?.has_custom_demo,
          demo_source: refreshedItem?.demo_source ?? null,
          demo_status: refreshedItem?.demo_status ?? null,
          demo_url: refreshedItem?.exercise_item?.demo_url || prev.demo_url,
          demo_url_input: "",
        }
      : prev));
    toast.success("Vídeo personalizado removido - volta a usar a demonstração padrão.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao remover vídeo personalizado.");
  } finally {
    setUploadingDemo(false);
  }
}, [assignmentItemDraft, loadUserWorkoutPlans, selectedStudentAssignmentId, selectedStudentId, toast]);

// ── "Meus vídeos" (professor) — vídeo próprio por exercise_item, vale por
// padrão pra todos os alunos dele (ver app/models/professor_exercise_media.py
// e a cascata de resolução em user_workout_plans.py). Mesma UI de URL vs
// arquivo do editor por-aluno acima, só que sem o conceito de "aluno".
const openProfessorMediaEditor = useCallback((item) => {
  setEditingProfessorMediaItemId(item.id);
  setProfessorMediaUrlInput(professorMediaByItemId[item.id]?.demo_url || "");
}, [professorMediaByItemId]);

const closeProfessorMediaEditor = useCallback(() => {
  setEditingProfessorMediaItemId(null);
  setProfessorMediaUrlInput("");
}, []);

const handleSaveProfessorDemoUrl = useCallback(async (itemId) => {
  if (!currentUser?.id || !itemId) return;
  const url = String(professorMediaUrlInput || "").trim();
  if (!url) return;
  setSavingProfessorMedia(true);
  try {
    const saved = (await api.post(
      `/professors/${currentUser.id}/exercise-media/${itemId}`,
      new URLSearchParams({ demo_url: url }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    )).data;
    setProfessorMediaByItemId((prev) => ({ ...prev, [itemId]: saved }));
    toast.success("Vídeo salvo - passa a valer pra todos os seus alunos sem vídeo específico.");
    closeProfessorMediaEditor();
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao salvar URL de demonstração.");
  } finally {
    setSavingProfessorMedia(false);
  }
}, [closeProfessorMediaEditor, currentUser, professorMediaUrlInput, toast]);

const handleUploadProfessorDemo = useCallback(async (itemId, file) => {
  if (!currentUser?.id || !itemId || !file) return;
  const sizeError = checkMediaFileSize(file);
  if (sizeError) {
    toast.error(sizeError);
    return;
  }
  setSavingProfessorMedia(true);
  try {
    const form = new FormData();
    form.append("file", file);
    const saved = (await api.post(
      `/professors/${currentUser.id}/exercise-media/${itemId}`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    )).data;
    setProfessorMediaByItemId((prev) => ({ ...prev, [itemId]: saved }));
    // Comprimido em segundo plano (app/jobs/video_compression_job.py) — já
    // vale pros alunos na hora, só troca pela versão otimizada depois.
    if (saved.demo_status === "processing") {
      toast.success("Vídeo enviado! Já vale pros alunos - a versão otimizada fica pronta em instantes.");
    } else {
      toast.success("Vídeo salvo - passa a valer pra todos os seus alunos sem vídeo específico.");
    }
    closeProfessorMediaEditor();
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao enviar vídeo.");
  } finally {
    setSavingProfessorMedia(false);
  }
}, [closeProfessorMediaEditor, currentUser, toast]);

const handleRemoveProfessorDemo = useCallback(async (itemId) => {
  if (!currentUser?.id || !itemId) return;
  setSavingProfessorMedia(true);
  try {
    await api.delete(`/professors/${currentUser.id}/exercise-media/${itemId}`);
    setProfessorMediaByItemId((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    toast.success("Vídeo removido - seus alunos voltam a ver a demonstração padrão.");
    closeProfessorMediaEditor();
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao remover vídeo.");
  } finally {
    setSavingProfessorMedia(false);
  }
}, [closeProfessorMediaEditor, currentUser, toast]);

const handleRemoveAnonAssignmentItem = useCallback(async (itemId) => {
  if (!selectedStudentAssignmentId) return;
  resetFeedback();
  setSaving(true);
  try {
    const updated = (await api.delete(`/user-workout-plans/${selectedStudentAssignmentId}/items/${itemId}`)).data;
    setStudentAssignments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    toast.success("Exercício removido do aluno.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao remover exercício.");
  } finally {
    setSaving(false);
  }
}, [resetFeedback, selectedStudentAssignmentId, toast]);

const confirmRemoveAnonAssignmentItem = useCallback((itemId) => {
  setConfirmMessage("Deseja remover este exercício avulso do vínculo?");
  setConfirmLabel("Remover");
  confirmActionRef.current = () => handleRemoveAnonAssignmentItem(itemId);
  setShowConfirm(true);
}, [handleRemoveAnonAssignmentItem]);

const handleRestorePlanItem = useCallback(async (planItemId) => {
  if (!selectedStudentAssignmentId) return;
  resetFeedback();
  setSaving(true);
  try {
    const updated = (await api.post(`/user-workout-plans/${selectedStudentAssignmentId}/items/by-plan/${planItemId}/unhide`)).data;
    setStudentAssignments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    toast.success("Item restaurado no vínculo.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao restaurar item.");
  } finally {
    setSaving(false);
  }
}, [resetFeedback, selectedStudentAssignmentId, toast]);

const confirmRestorePlanItem = useCallback((planItemId) => {
  setConfirmMessage("Deseja restaurar este exercício do treino padrão neste vínculo?");
  setConfirmLabel("Restaurar");
  confirmActionRef.current = () => handleRestorePlanItem(planItemId);
  setShowConfirm(true);
}, [handleRestorePlanItem]);

// Bi-set/superset/circuito no vínculo do aluno (user_workout_plan_items) —
// mesma ideia de group_workout_plan_items/ungroup, só que escopado à
// atribuição (POST/DELETE /user-workout-plans/{id}/items/group...).
// Personaliza só ESTE aluno, sem tocar no treino padrão.
const toggleGroupSelectItemAssignment = useCallback((itemId) => {
  setGroupSelectedIdsAssignment((prev) =>
    prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
  );
}, []);

const handleCancelGroupSelectAssignment = useCallback(() => {
  setGroupSelectModeAssignment(false);
  setGroupSelectedIdsAssignment([]);
}, []);

const handleCreateAssignmentGroup = useCallback(async () => {
  if (!selectedStudentAssignmentId) return;
  if (groupSelectedIdsAssignment.length < 2) {
    toast.error("Selecione ao menos 2 exercícios para agrupar.");
    return;
  }
  resetFeedback();
  setSaving(true);
  try {
    const updated = (await api.post(`/user-workout-plans/${selectedStudentAssignmentId}/items/group`, {
      item_ids: groupSelectedIdsAssignment,
    })).data;
    setStudentAssignments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    toast.success("Exercícios agrupados.");
    setGroupSelectedIdsAssignment([]);
    setGroupSelectModeAssignment(false);
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao agrupar exercícios.");
  } finally {
    setSaving(false);
  }
}, [selectedStudentAssignmentId, groupSelectedIdsAssignment, resetFeedback, toast]);

// Mesmo aviso de conflito do editor de template (achado #1 da auditoria
// UX), aplicado aos itens do VÍNCULO do aluno (user_workout_plan_items).
const groupConflictsAssignment = useMemo(() => {
  const assignment = studentAssignments.find((a) => a.id === selectedStudentAssignmentId);
  return findGroupConflicts(assignment?.items || [], groupSelectedIdsAssignment);
}, [studentAssignments, selectedStudentAssignmentId, groupSelectedIdsAssignment]);

// Mesmo aviso de séries divergentes do editor de template (achado #1 da
// revisão UX, 06/09/2026), aplicado ao vínculo do aluno.
const groupSetsMismatchAssignment = useMemo(() => {
  const assignment = studentAssignments.find((a) => a.id === selectedStudentAssignmentId);
  return findSetsMismatch(assignment?.items || [], groupSelectedIdsAssignment);
}, [studentAssignments, selectedStudentAssignmentId, groupSelectedIdsAssignment]);

// Mesmo rótulo dinâmico do botão "Agrupar" do editor de template, aplicado
// à personalização do aluno (achado "Later" da auditoria UX).
const groupSelectionTypeAssignment = useMemo(() => {
  if (groupSelectedIdsAssignment.length < 2) return null;
  const assignment = studentAssignments.find((a) => a.id === selectedStudentAssignmentId);
  const selectedItems = (assignment?.items || []).filter((it) => groupSelectedIdsAssignment.includes(it.id));
  return groupLabel(selectedItems);
}, [studentAssignments, selectedStudentAssignmentId, groupSelectedIdsAssignment]);

const handleCreateAssignmentGroupClick = useCallback(() => {
  if (groupConflictsAssignment.length === 0 && !groupSetsMismatchAssignment) {
    handleCreateAssignmentGroup();
    return;
  }
  const messages = [];
  if (groupConflictsAssignment.length > 0) {
    const desc = groupConflictsAssignment.map((c) => c.names.join(" + ")).join("; ");
    messages.push(`Isso vai desfazer o grupo com ${desc} neste vínculo, porque nem todos os exercícios dele estão selecionados.`);
  }
  if (groupSetsMismatchAssignment) {
    const desc = groupSetsMismatchAssignment.map((s) => `${s.name} (${s.sets}x)`).join(", ");
    messages.push(`Os exercícios selecionados têm números de série diferentes - ${desc}. O de menos séries vai terminar antes e sair do rodízio durante o treino.`);
  }
  messages.push("Deseja continuar?");
  setConfirmMessage(messages.join(" "));
  setConfirmLabel("Agrupar mesmo assim");
  confirmActionRef.current = handleCreateAssignmentGroup;
  setShowConfirm(true);
}, [groupConflictsAssignment, groupSetsMismatchAssignment, handleCreateAssignmentGroup]);

const handleUngroupAssignmentBlock = useCallback(async (groupId) => {
  if (!selectedStudentAssignmentId) return;
  resetFeedback();
  setSaving(true);
  try {
    const updated = (await api.delete(`/user-workout-plans/${selectedStudentAssignmentId}/items/group/${groupId}`)).data;
    setStudentAssignments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    toast.success("Grupo desfeito - exercícios voltaram a ser avulsos.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao desagrupar.");
  } finally {
    setSaving(false);
  }
}, [selectedStudentAssignmentId, resetFeedback, toast]);

// Move um bloco inteiro (item avulso ou grupo contíguo) do VÍNCULO uma
// posição pra cima/baixo — mesma lógica de handleMoveBlock (template),
// mas usando os endpoints de item da atribuição.
const handleMoveAssignmentBlock = useCallback(async (block, dir) => {
  if (!selectedStudentAssignmentId) return;
  const assignment = studentAssignments.find((a) => a.id === selectedStudentAssignmentId);
  if (!assignment || !Array.isArray(assignment.items) || !assignment.items.length) return;
  const sorted = [...assignment.items].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const blocks = buildItemBlocks(sorted);
  const curIdx = blocks.findIndex((b) =>
    b.kind === "group" && block.kind === "group"
      ? b.groupId === block.groupId
      : b.kind === "item" && block.kind === "item" && b.item.id === block.item.id
  );
  if (curIdx < 0) return;
  const tgtIdx = curIdx + dir;
  if (tgtIdx < 0 || tgtIdx >= blocks.length) return;

  const newBlocks = [...blocks];
  const [moved] = newBlocks.splice(curIdx, 1);
  newBlocks.splice(tgtIdx, 0, moved);
  const flatNewOrder = newBlocks.flatMap((b) => (b.kind === "group" ? b.items : [b.item]));
  // NOTA (bug fix 2026-09-22, revisado 2026-09-23): o backend normaliza
  // order_index pra valores consecutivos 1-based em
  // serialize_effective_assignment (ver comentário "Normalize order_index"
  // em app/api/user_workout_plans.py) — MAS só na LEITURA, sem persistir
  // isso de volta no banco. group_user_workout_plan_items (endpoint de
  // agrupar) grava outro valor bruto pros itens. Resultado: o order_index
  // que o frontend vê (1-based, normalizado) pode divergir do valor bruto
  // real no banco pra itens que este reorder NÃO tocaria. Mandar só o
  // "diff" (itens cujo order_index normalizado mudou) deixava esses itens
  // intocados com um valor bruto desatualizado — que podia coincidir com o
  // novo valor de um item que ESTE reorder acabou de mover, duplicando
  // order_index e quebrando a adjacência de um grupo (reproduzido: agrupar
  // 2 itens em bi-set e, na sequência, mover o bloco — um 3º item "invadia"
  // o meio do grupo). Corrigido mandando a lista INTEIRA renumerada
  // (1-based) a cada reorder, nunca só os itens que mudaram na visão do
  // frontend — auto-corrige qualquer desalinhamento anterior em vez de
  // silenciosamente propagá-lo. Ver docs/07-issues.md.
  const changes = flatNewOrder.map((it, idx) => ({ id: it.id, order_index: idx + 1 }));

  setSaving(true);
  try {
    // Lote atômico (endpoint dedicado) em vez de 1 PATCH por item em
    // sequência — mesmo motivo do handleMoveBlock acima (ver R248/
    // docs/07-issues.md): uma falha de rede no meio do loop podia deixar
    // a ficha do aluno com parte dos itens reordenados e parte não.
    const resp = await api.patch(`/user-workout-plans/${selectedStudentAssignmentId}/items/reorder`, { items: changes });
    setStudentAssignments((prev) => prev.map((a) => (a.id === resp.data.id ? resp.data : a)));
    toast.success("Ordem atualizada.");
  } catch (err) {
    toast.error(err.response?.data?.detail || err.message || "Erro ao reordenar.");
  } finally {
    setSaving(false);
  }
}, [selectedStudentAssignmentId, studentAssignments, toast]);

// Precisa ser declarada ANTES do useMemo de workoutAdminContextValue logo
// abaixo -- a fábrica do useMemo roda de imediato durante o render e lê
// handleConfirmGenerated nesse momento; se a const só fosse declarada depois
// (como estava antes), o identificador ainda estaria em temporal dead zone
// nesse ponto, gerando "ReferenceError: can't access lexical declaration
// 'handleConfirmGenerated' before initialization" (ver docs/07-issues.md).
const handleConfirmGenerated = useCallback(async () => {
  if (!aiPreview) return;
  setAiConfirming(true);
  try {
    const { data } = await api.post("/ai/generate-workout-plan/confirm", {
      student_id: selectedStudentId,
      days: aiPreview.days,
      rationale: aiPreview.rationale || null,
      ai_usage_log_id: aiPreview.ai_usage_log_id ?? null,
    });
    setAiSuccess({
      days_created: data.days_created,
      days: data.days,
      rationale: data.rationale,
      message: data.message,
    });
    setAiPreview(null);
  } catch (err) {
    toast?.error(err?.response?.data?.detail || err?.message || "Erro ao salvar treino. Tente novamente.");
  } finally {
    setAiConfirming(false);
  }
}, [aiPreview, selectedStudentId, toast]);

// Value do WorkoutAdminContext consumido por StudentsTab.jsx (issue 012,
// docs/07-issues.md) -- mesmos ~39 valores que antes eram passados como
// props manualmente. useMemo pra não recriar o objeto em todo render só
// porque um estado não relacionado mudou (ainda recalcula quando qualquer
// item da lista de deps muda, que é a maioria deles -- não é uma otimização
// de performance, só evita um objeto novo por render à toa).
const workoutAdminContextValue = useMemo(() => ({
  filteredStudents,
  visibleStudents,
  professorsById,
  studentQuery,
  setStudentQuery,
  selectedStudentId,
  setSelectedStudentId,
  selectedStudent,
  planPickerRef,
  planPickerOpen,
  setPlanPickerOpen,
  planPickerQuery,
  setPlanPickerQuery,
  planPickerActiveIndex,
  setPlanPickerActiveIndex,
  planPickerResults,
  selectedAssignmentPlan,
  assignmentPlanId,
  setAssignmentPlanId,
  assignForm,
  setAssignForm,
  handleAssignPlanToStudent,
  saving,
  setSaving,
  hasRationale,
  setRationaleModal,
  studentAssignments,
  setStudentAssignments,
  setShowAIModal,
  setAiSuccess,
  setAiPreview,
  setAiHasAdjusted,
  setAiRationaleOpen,
  selectedStudentAssignmentId,
  setSelectedStudentAssignmentId,
  handleSyncStudentAssignment,
  handleRemoveStudentAssignment,
  setOpenStudentAssignmentModal,
  setDayVideosAssignment,
  // -- usados só por StudentAssignmentModal.jsx --
  selectedStudentAssignment,
  studentAssignmentModalRef,
  closeStudentAssignmentModal,
  assignmentMetaForm,
  setAssignmentMetaForm,
  handleUpdateAssignmentMeta,
  confirmActionRef,
  confirmRemoveAnonAssignmentItem,
  confirmRestorePlanItem,
  groupConflictsAssignment,
  groupSelectModeAssignment,
  setGroupSelectModeAssignment,
  groupSelectedIdsAssignment,
  groupSelectionTypeAssignment,
  handleCancelGroupSelectAssignment,
  handleCreateAssignmentGroupClick,
  handleMoveAssignmentBlock,
  handleUngroupAssignmentBlock,
  openAddAnonItem,
  openAssignmentItemEditor,
  resetFeedback,
  setConfirmLabel,
  setConfirmMessage,
  setShowConfirm,
  toggleGroupSelectItemAssignment,
  // -- usados só por PlansTab.jsx --
  filteredPlans,
  listablePlans,
  selectedPlanId,
  setSelectedPlanId,
  selectedPlan,
  resetPlanItemEditor,
  planQuery,
  setPlanQuery,
  isStandardPlan,
  isBlankPlan,
  openCreatePlanModal,
  canManageSelectedPlan,
  itemsExpanded,
  setItemsExpanded,
  isMobile,
  openCreatePlanItemModal,
  openEditPlanModal,
  handleDeletePlan,
  openPlanActionsMenu,
  setOpenPlanActionsMenu,
  groupSelectMode,
  setGroupSelectMode,
  groupSelectedIds,
  groupConflicts,
  handleCreateGroupClick,
  groupSelectionType,
  handleCancelGroupSelect,
  handleMoveBlock,
  handleUngroupBlock,
  toggleGroupSelectItem,
  openEditPlanItemModal,
  handleDeletePlanItem,
  // -- usados só por AIGenerateModal.jsx --
  aiConfirming,
  aiForm,
  setAiForm,
  aiGenMsgIdx,
  aiGenerating,
  aiHasAdjusted,
  aiPreview,
  aiSuccess,
  handleConfirmGenerated,
  loadUserWorkoutPlans,
  setAiConfirmOpen,
}), [
  filteredStudents,
  visibleStudents,
  professorsById,
  studentQuery,
  selectedStudentId,
  selectedStudent,
  planPickerOpen,
  planPickerQuery,
  planPickerActiveIndex,
  planPickerResults,
  selectedAssignmentPlan,
  assignmentPlanId,
  assignForm,
  handleAssignPlanToStudent,
  saving,
  hasRationale,
  studentAssignments,
  selectedStudentAssignmentId,
  handleSyncStudentAssignment,
  handleRemoveStudentAssignment,
  selectedStudentAssignment,
  closeStudentAssignmentModal,
  assignmentMetaForm,
  handleUpdateAssignmentMeta,
  confirmRemoveAnonAssignmentItem,
  confirmRestorePlanItem,
  groupConflictsAssignment,
  groupSelectModeAssignment,
  groupSelectedIdsAssignment,
  groupSelectionTypeAssignment,
  handleCancelGroupSelectAssignment,
  handleCreateAssignmentGroupClick,
  handleMoveAssignmentBlock,
  handleUngroupAssignmentBlock,
  openAddAnonItem,
  openAssignmentItemEditor,
  resetFeedback,
  toggleGroupSelectItemAssignment,
  filteredPlans,
  listablePlans,
  selectedPlanId,
  selectedPlan,
  resetPlanItemEditor,
  planQuery,
  isStandardPlan,
  isBlankPlan,
  openCreatePlanModal,
  canManageSelectedPlan,
  itemsExpanded,
  isMobile,
  openCreatePlanItemModal,
  openEditPlanModal,
  handleDeletePlan,
  openPlanActionsMenu,
  groupSelectMode,
  groupSelectedIds,
  groupConflicts,
  handleCreateGroupClick,
  groupSelectionType,
  handleCancelGroupSelect,
  handleMoveBlock,
  handleUngroupBlock,
  toggleGroupSelectItem,
  openEditPlanItemModal,
  handleDeletePlanItem,
  aiConfirming,
  aiForm,
  aiGenMsgIdx,
  aiGenerating,
  aiHasAdjusted,
  aiPreview,
  aiSuccess,
  handleConfirmGenerated,
  loadUserWorkoutPlans,
]);

if (!open) return null;

const modalContent = (
  <div className={styles.wamOverlay} onClick={(aiGenerating || aiConfirming) ? undefined : onClose}>
    <div
      className={styles.wamModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wam-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ModalHeader onClose={onClose} disabled={aiGenerating} />
      <div className={styles.wamHeader}>
        <div>
          <h2 id="wam-title">Treinos</h2>
          <p>{modalDescription}</p>
        </div>
      </div>

      <div className={styles.wamTabs} role="tablist">
        {isAdmin && (
          <TabButton active={tab === "catalog"} onClick={() => setTab("catalog")}>
            Categorias & Exercícios
          </TabButton>
        )}

        <TabButton active={tab === "plans"} onClick={() => setTab("plans")}>
          Treinos & Vínculos
        </TabButton>

        <TabButton active={tab === "students"} onClick={() => setTab("students")}>
          Alunos & treinos
        </TabButton>

        {isProfessor && (
          <TabButton active={tab === "myvideos"} onClick={() => setTab("myvideos")}>
            Meus exercícios & vídeos
          </TabButton>
        )}
      </div>

      <div className={styles.wamBody}>
        <div className={styles.wamContent}>
          {loading && <div className={styles.wamNote}>Carregando dados...</div>}

          {!loading && isAdmin && tab === "catalog" && (
            <CatalogTab
              categories={categories}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
              resetExerciseItemEditor={resetExerciseItemEditor}
              selectedCategory={selectedCategory}
              exerciseItemSearch={exerciseItemSearch}
              setExerciseItemSearch={setExerciseItemSearch}
              categoryItems={categoryItems}
              filteredCategoryItems={filteredCategoryItems}
              startCreateExerciseItem={startCreateExerciseItem}
              setDemoExerciseItem={setDemoExerciseItem}
              startEditExerciseItem={startEditExerciseItem}
              handleDeleteExerciseItem={handleDeleteExerciseItem}
              saving={saving}
              showInactiveExercises={showInactiveExercises}
              setShowInactiveExercises={setShowInactiveExercises}
            />
          )}

          {!loading && isProfessor && tab === "myvideos" && (
            <MyVideosTab
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
              closeProfessorMediaEditor={closeProfessorMediaEditor}
              categories={categories}
              selectedCategory={selectedCategory}
              categoryItems={categoryItems}
              filteredCategoryItems={filteredCategoryItems}
              exerciseItemSearch={exerciseItemSearch}
              setExerciseItemSearch={setExerciseItemSearch}
              startCreateExerciseItem={startCreateExerciseItem}
              currentUser={currentUser}
              professorMediaByItemId={professorMediaByItemId}
              editingProfessorMediaItemId={editingProfessorMediaItemId}
              professorMediaUrlInput={professorMediaUrlInput}
              setProfessorMediaUrlInput={setProfessorMediaUrlInput}
              savingProfessorMedia={savingProfessorMedia}
              handleSaveProfessorDemoUrl={handleSaveProfessorDemoUrl}
              handleUploadProfessorDemo={handleUploadProfessorDemo}
              setDemoExerciseItem={setDemoExerciseItem}
              openProfessorMediaEditor={openProfessorMediaEditor}
              handleRemoveProfessorDemo={handleRemoveProfessorDemo}
              startEditExerciseItem={startEditExerciseItem}
              handleDeleteExerciseItem={handleDeleteExerciseItem}
              saving={saving}
            />
          )}

                  {/* ── MODAL de criação/edição de exercício ── */}
                  {openExerciseItemModal && <ExerciseItemModal
              editingExerciseItemId={editingExerciseItemId}
              exerciseItemFile={exerciseItemFile}
              exerciseItemForm={exerciseItemForm}
              exerciseItemModalRef={exerciseItemModalRef}
              askRemoveExerciseMedia={askRemoveExerciseMedia}
              handleSaveExerciseItem={handleSaveExerciseItem}
              resetExerciseItemEditor={resetExerciseItemEditor}
              saving={saving}
              selectedCategory={selectedCategory}
              setDemoExerciseItem={setDemoExerciseItem}
              setExerciseItemFile={setExerciseItemFile}
              setExerciseItemForm={setExerciseItemForm}
              requireClassification={isProfessor}
            />}

          {!loading && tab === "plans" && (
            <PlansTab />
          )}

          {!loading && tab === "students" && (
            <StudentsTab />
          )}

              {openPlanModal && (
                <PlanModal
              closePlanModal={closePlanModal}
              planModalRef={planModalRef}
              editingPlanId={editingPlanId}
              handleSavePlan={handleSavePlan}
              planForm={planForm}
              setPlanForm={setPlanForm}
              saving={saving}
            />
              )}

              {openPlanItemModal && (
                <PlanItemModal
              planItemModalRef={planItemModalRef}
              closePlanItemModal={closePlanItemModal}
              editingPlanItemId={editingPlanItemId}
              categories={categories}
              handlePlanCategoryChange={handlePlanCategoryChange}
              planCategoryItems={planCategoryItems}
              demoUrlFor={demoUrlFor}
              ownerId={currentUser?.id}
              previewExerciseDemo={previewExerciseDemo}
              planItemForm={planItemForm}
              setPlanItemForm={setPlanItemForm}
              applyPreset={applyPreset}
              confirmDeletePreset={confirmDeletePreset}
              handleCreatePreset={handleCreatePreset}
              handleSavePlanItem={handleSavePlanItem}
              newPresetForm={newPresetForm}
              setNewPresetForm={setNewPresetForm}
              presets={presets}
              presetsLoading={presetsLoading}
              saving={saving}
              savingPreset={savingPreset}
              setShowCreatePresetForm={setShowCreatePresetForm}
              showCreatePresetForm={showCreatePresetForm}
            />
              )}

              {openStudentAssignmentModal && selectedStudentAssignment && (
                <StudentAssignmentModal />
              )}

              {openAssignmentItemModal && assignmentItemDraft && (
                <AssignmentItemModal
              assignmentItemDraft={assignmentItemDraft}
              setAssignmentItemDraft={setAssignmentItemDraft}
              assignmentItemModalRef={assignmentItemModalRef}
              closeAssignmentItemEditor={closeAssignmentItemEditor}
              applyPreset={applyPreset}
              confirmDeletePreset={confirmDeletePreset}
              handleCreatePreset={handleCreatePreset}
              handleRemoveStudentDemo={handleRemoveStudentDemo}
              handleSaveAssignmentItem={handleSaveAssignmentItem}
              handleSaveStudentDemoUrl={handleSaveStudentDemoUrl}
              handleUploadStudentDemo={handleUploadStudentDemo}
              newPresetForm={newPresetForm}
              setNewPresetForm={setNewPresetForm}
              presets={presets}
              presetsLoading={presetsLoading}
              saving={saving}
              savingPreset={savingPreset}
              setShowCreatePresetForm={setShowCreatePresetForm}
              showCreatePresetForm={showCreatePresetForm}
              uploadingDemo={uploadingDemo}
            />
              )}

              {openAddAnonItemModal && (
                <AddAnonItemModal
              addAnonItemModalRef={addAnonItemModalRef}
              closeAddAnonItem={closeAddAnonItem}
              anonExerciseItems={anonExerciseItems}
              demoUrlFor={demoUrlFor}
              ownerId={currentUser?.id}
              previewExerciseDemo={previewExerciseDemo}
              anonItemForm={anonItemForm}
              setAnonItemForm={setAnonItemForm}
              applyPreset={applyPreset}
              categories={categories}
              confirmDeletePreset={confirmDeletePreset}
              handleAnonCategoryChange={handleAnonCategoryChange}
              handleCreatePreset={handleCreatePreset}
              handleSaveAnonAssignmentItem={handleSaveAnonAssignmentItem}
              newPresetForm={newPresetForm}
              setNewPresetForm={setNewPresetForm}
              presets={presets}
              presetsLoading={presetsLoading}
              saving={saving}
              savingPreset={savingPreset}
              setShowCreatePresetForm={setShowCreatePresetForm}
              showCreatePresetForm={showCreatePresetForm}
            />
              )}
        </div>
      </div>
    </div>
  </div>
);


const handleGenerate = async () => {
  setAiConfirmOpen(false);
  setAiGenerating(true);
  const calcAge = (bd) => {
    if (!bd) return null;
    const today = new Date();
    const birth = new Date(bd);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };
  const body = {
    student_id: selectedStudentId,
    goals: aiForm.goals,
    days_per_week: aiForm.days_per_week,
    session_duration_minutes: aiForm.session_duration_minutes,
    fitness_level: (() => {
      const al = selectedStudent?.activity_level;
      if (al === "muito_ativo") return "avançado";
      if (al === "ativo" || al === "moderado") return "intermediário";
      return "iniciante";
    })(),
    restrictions: aiForm.restrictions || null,
    focus: aiForm.focus || null,
    gender: selectedStudent?.gender || null,
    age: calcAge(selectedStudent?.birth_date),
    secondary_goals: aiForm.secondary_goals,
    include_cardio: aiForm.include_cardio,
    cardio_type: aiForm.cardio_type,
    cardio_duration_minutes: aiForm.cardio_duration_minutes,
    teacher_notes: aiForm.teacher_notes || null,
    equipment: aiForm.equipment,
  };
  try {
    // Gera prévia (não persiste) — mesmo padrão preview/confirm do SelfWorkoutModal.
    // Permite "← Ajustar" na tela seguinte sem sobrescrever de imediato o plano do aluno.
    const { data } = await api.post("/ai/generate-workout-plan/preview", body);
    setAiPreview({
      days: data.days,
      rationale: data.rationale,
      rationale_student: data.rationale_student,
      ai_usage_log_id: data.ai_usage_log_id,
    });
    setAiRationaleOpen(true);
  } catch (err) {
    toast?.error(err?.response?.data?.detail || err?.message || "Erro ao gerar treino. Tente novamente.");
  } finally {
    setAiGenerating(false);
  }
};

return createPortal(
  <WorkoutAdminContext.Provider value={workoutAdminContextValue}>
    {modalContent}
    <ConfirmDialog
      open={showConfirm}
      title={confirmTitle}
      message={confirmMessage}
      confirmLabel={confirmLabel}
      confirmVariant={confirmVariant}
      onClose={() => { setShowConfirm(false); setConfirmTitle(undefined); setConfirmVariant(undefined); }}
      onConfirm={async () => {
        setShowConfirm(false);
        setConfirmTitle(undefined);
        setConfirmVariant(undefined);
        try {
          if (confirmActionRef.current) await confirmActionRef.current();
        } catch (_) {
          // feedback already managed
        }
      }}
    />
    {demoExerciseItem && (
      <ExerciseDemoModal
        open={!!demoExerciseItem}
        exerciseItem={demoExerciseItem}
        onClose={() => setDemoExerciseItem(null)}
      />
    )}

    {dayVideosAssignment && <DayVideosAssignmentModal
              dayVideosAssignment={dayVideosAssignment}
              setDayVideosAssignment={setDayVideosAssignment}
              setDemoExerciseItem={setDemoExerciseItem}
            />}

    {/* ── Modal de geração por IA ── */}
    {showAIModal && <AIGenerateModal />}

    {/* ── Confirm de geração por IA ── */}
    {aiConfirmOpen && <AIConfirmModal
              aiForm={aiForm}
              selectedStudent={selectedStudent}
              handleGenerate={handleGenerate}
              setAiConfirmOpen={setAiConfirmOpen}
            />}
    {rationaleModal.open && (
      <WorkoutRationaleModal
        rationale={rationaleModal.text}
        onClose={() => setRationaleModal({ open: false, text: null })}
        professorLogoUrl={currentUser?.professor_logo_url}
      />
    )}

  </WorkoutAdminContext.Provider>,

  document.body
);
}